import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ShieldCheck, Clapperboard, ShieldAlert, EyeOff, Minus,
  Castle, Wrench, Swords, Layers, Gem,
} from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
  BubbleController,
} from 'chart.js';
import { Radar, Bubble } from 'react-chartjs-2';
import client from '../api/client';
import '../App.css';

ChartJS.register(
  RadialLinearScale, PointElement, LineElement, Filler,
  Tooltip, Legend, LinearScale, CategoryScale, BubbleController
);

const RADAR_LABELS = [
  'Maturit\u00e9 Data',
  'Fr\u00e9quence d\u2019\u00e9tudes',
  'Culture Data-driven',
  'Proactivit\u00e9',
  'Engagement',
];

const QUADRANT_LABELS = {
  tl: 'Intuitif engag\u00e9',
  tr: 'Data champion',
  bl: 'En d\u00e9couverte',
  br: 'Data curieux',
};

const SECTEUR_COLORS = {
  'Finance & Banque': '#fabc02',
  'Commerce & E-commerce': '#f97316',
  'T\u00e9l\u00e9coms & Tech': '#8b5cf6',
  'Sant\u00e9': '#10b981',
  'Services Publics & Gouvernement': '#3b82f6',
  '\u00c9nergie & Infrastructure': '#f59e0b',
  'Non renseign\u00e9': '#9ca3af',
};

// Inflate displayed total: +1 every 30 minutes starting 2026-04-07 11:19 Casablanca (UTC+1),
// capped after 3 days (144 increments).
function getInflatedTotal(realTotal) {
  if (typeof realTotal !== 'number') realTotal = Number(realTotal) || 0;
  const START_MS = Date.UTC(2026, 3, 7, 10, 19, 0); // 11:19 UTC+1 → 10:19 UTC
  const HALF_HOUR = 30 * 60 * 1000;
  const MAX_INCREMENTS = 144; // 3 days * 24 hours * 2
  const elapsed = Date.now() - START_MS;
  if (elapsed <= 0) return realTotal;
  const increments = Math.min(MAX_INCREMENTS, Math.floor(elapsed / HALF_HOUR));
  return realTotal + increments;
}

function Results({ surveyId, sessionToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isGeneral = sessionToken === 'general';

  // Persist token
  useEffect(() => {
    if (!isGeneral && sessionToken && surveyId) {
      try {
        const sessions = JSON.parse(localStorage.getItem('immersion_sessions') || '{}');
        sessions[surveyId] = sessionToken;
        localStorage.setItem('immersion_sessions', JSON.stringify(sessions));
      } catch { /* ignore */ }
    }
  }, [isGeneral, sessionToken, surveyId]);

  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    let retries = 0;
    const maxRetries = 3;

    const fetchResults = () => {
      const request = isGeneral
        ? client.get('/chatbot/results/general', { params: { survey_id: surveyId } })
        : client.get('/chatbot/results', { params: { token: sessionToken, survey_id: surveyId } });

      request
        .then((res) => {
          const d = res.data?.data || null;
          if (d) setData(d);

          // Auto-poll if any word cloud has pending keyword
          if (d && !isGeneral && d.charts && pollCount < 12) {
            const hasPending = d.charts.some(
              (c) => c.type === 'word_cloud' && c.data?.my_keyword_pending
            );
            if (hasPending) {
              setTimeout(() => setPollCount((c) => c + 1), 5000);
            }
          }
          setLoading(false);
        })
        .catch(() => {
          if (retries < maxRetries) {
            retries++;
            setTimeout(fetchResults, 2000 * retries);
          } else {
            if (pollCount === 0) setData(null);
            setLoading(false);
          }
        });
    };

    fetchResults();
  }, [surveyId, sessionToken, isGeneral, pollCount]);

  if (loading) {
    return (
      <div className="results">
        <div className="results__loading">
          <div className="results__spinner" />
          <p>{"Calcul des r\u00e9sultats\u2026"}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="results">
        <div className="results__loading">
          <p>Impossible de charger les résultats.</p>
          <p>Veuillez rafraîchir la page.</p>
        </div>
      </div>
    );
  }

  const isConfigDriven = data.config_driven === true;

  return (
    <div className="results">
      <header className="results__hero">
        <img
          src={`${process.env.PUBLIC_URL}/IMMERSION CONSULTING GROUP NOIR.png`}
          alt="Immersion Consulting Group"
          className="page-logo"
        />
        <div className="results__hero-content">
          <span className="results__badge">{isGeneral ? "Résultats généraux" : "Vos résultats"}</span>
          <h1 className="results__title">{data.survey.title}</h1>
          <p className="results__subtitle">
            {isGeneral
              ? `Voici les résultats généraux du sondage — ${getInflatedTotal(data.total_respondents)} participants.`
              : `Vos résultats personnalisés, comparés à ${getInflatedTotal(data.total_respondents)} participants.`}
          </p>
        </div>
      </header>

      <div className="results__body">
        {/* ── Profile cards (surveys with scoring) ── */}
        {isConfigDriven && !isGeneral && data.my_profile && data.profiles && (
          <ProfileResults myProfile={data.my_profile} profileDefs={data.profiles} />
        )}

        {/* ── Config-driven charts (survey 1003+) ── */}
        {isConfigDriven && (data.charts || []).map((chart, i) => (
          <ConfigChart
            key={chart.data_key || i}
            chart={chart}
            isGeneral={isGeneral}
            animDelay={0.3 + i * 0.1}
            resultsData={data}
          />
        ))}

        {/* ── Legacy charts (survey 1002) ── */}
        {!isConfigDriven && (
          <>
            {!isGeneral && data.my_data && (
              <section className="results__card results__card--radar">
                <h2 className="results__card-title">Votre profil Data</h2>
                <p className="results__card-desc">
                  Cinq dimensions clés qui définissent votre rapport à la donnée.
                </p>
                <div className="results__chart-wrap results__chart-wrap--square">
                  <RadarChart data={data} />
                </div>
              </section>
            )}

            <section className="results__card results__card--bubble">
              <h2 className="results__card-title">Perception vs Pratique</h2>
              <p className="results__card-desc">
                {isGeneral
                  ? "Chaque bulle représente un participant. Axe X = approche décisionnelle, Axe Y = fréquence d'études."
                  : "Chaque bulle représente un participant. Axe X = approche décisionnelle, Axe Y = fréquence d'études, taille = engagement."}
              </p>
              <div className="results__quadrant-labels">
                <span className="results__qlabel results__qlabel--tl">{QUADRANT_LABELS.tl}</span>
                <span className="results__qlabel results__qlabel--tr">{QUADRANT_LABELS.tr}</span>
                <span className="results__qlabel results__qlabel--bl">{QUADRANT_LABELS.bl}</span>
                <span className="results__qlabel results__qlabel--br">{QUADRANT_LABELS.br}</span>
              </div>
              <div className="results__chart-wrap results__chart-wrap--wide">
                <BubbleChart data={data} isGeneral={isGeneral} />
              </div>
            </section>

            <section className="results__card results__card--radial">
              <h2 className="results__card-title">Vue d'ensemble par secteur</h2>
              <p className="results__card-desc">
                {isGeneral
                  ? "Chaque trait représente un participant, regroupé par secteur d'activité."
                  : "Chaque trait représente un participant, regroupé par secteur d'activité. Votre profil est mis en évidence."}
              </p>
              <div className="results__chart-wrap results__chart-wrap--square">
                <NightingaleChart data={data} isGeneral={isGeneral} />
              </div>
            </section>
          </>
        )}

        {/* ── Synthesis ── */}
        {!isGeneral && (
          <SynthesisSection surveyId={surveyId} sessionToken={sessionToken} />
        )}

        {isGeneral && data.syntheses && data.syntheses.length > 0 && (
          <GeneralSynthesisSection syntheses={data.syntheses} />
        )}

        {isGeneral && (
          <div className="results-list__cta" style={{ textAlign: 'center', paddingTop: 8 }}>
            <a href="/surveys" className="btn btn--primary">
              {"Participer au sondage"}
              <span className="btn__arrow">&rarr;</span>
            </a>
          </div>
        )}

        <div className="results__accueil-cta">
          <a href="https://immersionconsultinggroup.com/" className="btn btn--outline">
            Accueil
          </a>
        </div>
      </div>
    </div>
  );
}

/* ── Config Chart Router ───────────────────────────── */
function ConfigChart({ chart, isGeneral, animDelay, resultsData }) {
  const d = chart.data || {};
  return (
    <section className="results__card" style={{ animationDelay: `${animDelay}s` }}>
      <h2 className="results__card-title">{chart.title}</h2>
      <p className="results__card-desc">{chart.description}</p>

      {chart.type === 'nightingale_rose' && (
        <div className="results__chart-wrap results__chart-wrap--square">
          <NightingaleRoseChart data={d} isGeneral={isGeneral} />
        </div>
      )}

      {chart.type === 'bubble_scatter' && (
        <div className="results__chart-wrap results__chart-wrap--wide">
          <ConfigBubbleChart data={d} isGeneral={isGeneral} chart={chart} />
        </div>
      )}

      {chart.type === 'nightingale_lines' && (
        <div className="results__chart-wrap results__chart-wrap--square">
          <ConfigNightingaleLines data={d} isGeneral={isGeneral} />
        </div>
      )}

      {chart.type === 'word_cloud' && (
        <div className="results__chart-wrap">
          <WordCloud data={d} />
        </div>
      )}

      {chart.type === 'matrix_bubble' && resultsData?.all_profiles && (
        <div className="results__chart-wrap results__chart-wrap--square">
          <MatrixBubble
            allProfiles={resultsData.all_profiles}
            myProfile={isGeneral ? null : resultsData.my_profile}
          />
        </div>
      )}

      {chart.type === 'scatter_aggregate_bar' && resultsData?.all_profiles && (
        <div className="results__chart-wrap">
          <ScatterAggregateBar
            allProfiles={resultsData.all_profiles}
            myProfile={isGeneral ? null : resultsData.my_profile}
            myResponseId={isGeneral ? null : resultsData.my_response_id}
            profileDefs={resultsData.profiles}
          />
        </div>
      )}
    </section>
  );
}

/* ── ① Radar ──────────────────────────────────────── */
function RadarChart({ data }) {
  const my = data.my_data?.radar || {};
  const avgRadar = useMemo(() => {
    const all = data.all_respondents || [];
    const keys = Object.keys(my);
    const avg = {};
    keys.forEach((k) => {
      avg[k] = Math.round(all.reduce((s, r) => s + (r.radar?.[k] || 0), 0) / (all.length || 1));
    });
    return avg;
  }, [data.all_respondents, my]);

  const chartData = {
    labels: RADAR_LABELS,
    datasets: [
      {
        label: 'Vous',
        data: [my.maturite_data, my.frequence_etudes, my.culture_datadriven, my.proactivite, my.engagement],
        backgroundColor: 'rgba(250, 188, 2, .18)',
        borderColor: '#fabc02',
        borderWidth: 2.5,
        pointBackgroundColor: '#fabc02',
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: 'Moyenne',
        data: [avgRadar.maturite_data, avgRadar.frequence_etudes, avgRadar.culture_datadriven, avgRadar.proactivite, avgRadar.engagement],
        backgroundColor: 'rgba(156, 163, 175, .1)',
        borderColor: '#9ca3af',
        borderWidth: 1.5,
        borderDash: [5, 5],
        pointBackgroundColor: '#9ca3af',
        pointRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: { legend: { position: 'bottom', labels: { font: { family: "'Plus Jakarta Sans'" }, usePointStyle: true, padding: 20 } } },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: { stepSize: 25, backdropColor: 'transparent', font: { size: 10 } },
        grid: { color: 'rgba(0,0,0,.06)' },
        angleLines: { color: 'rgba(0,0,0,.06)' },
        pointLabels: { font: { size: 12, family: "'Plus Jakarta Sans'", weight: '600' }, color: '#1a1a2e' },
      },
    },
  };

  return <Radar data={chartData} options={options} />;
}

/* ── ② Bubble Scatter ─────────────────────────────── */
function BubbleChart({ data, isGeneral }) {
  const myId = isGeneral ? null : data.my_response_id;
  const others = (data.all_respondents || []).filter((r) => r.response_id !== myId);
  const me = isGeneral ? null : data.my_data;

  const datasets = useMemo(() => {
    const ds = [
      {
        label: 'Participants',
        data: others.map((r) => ({
          x: r.decision_score,
          y: r.research_freq,
          r: r.has_audio ? 10 : 6,
        })),
        backgroundColor: 'rgba(156, 163, 175, .35)',
        borderColor: 'rgba(156, 163, 175, .5)',
        borderWidth: 1,
      },
    ];

    if (me) {
      ds.push({
        label: 'Vous',
        data: [{ x: me.decision_score, y: me.research_freq, r: me.has_audio ? 14 : 10 }],
        backgroundColor: 'rgba(250, 188, 2, .7)',
        borderColor: '#fabc02',
        borderWidth: 2.5,
      });
    }

    return ds;
  }, [others, me]);

  const chartData = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { family: "'Plus Jakarta Sans'" }, usePointStyle: true, padding: 20 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const d = ctx.raw;
            return `D\u00e9cision: ${Math.round(d.x)}, Recherche: ${Math.round(d.y)}`;
          },
        },
      },
    },
    scales: {
      x: {
        min: 0.5, max: 4.5,
        title: { display: true, text: 'Approche d\u00e9cisionnelle \u2192', font: { family: "'Plus Jakarta Sans'", size: 12, weight: '600' } },
        grid: { color: 'rgba(0,0,0,.04)' },
        ticks: { stepSize: 1, callback: (v) => ['', 'Instinct', 'M\u00e9lange', 'Data first', 'Full data'][v] || '' },
      },
      y: {
        min: 0.5, max: 5.5,
        title: { display: true, text: 'Fr\u00e9quence d\'\u00e9tudes \u2192', font: { family: "'Plus Jakarta Sans'", size: 12, weight: '600' } },
        grid: { color: 'rgba(0,0,0,.04)' },
        ticks: { stepSize: 1 },
      },
    },
  };

  return <Bubble data={chartData} options={options} />;
}

/* ── ③ Waffle Dot Matrix ──────────────────────────── */
function WaffleDots({ data }) {
  const total = data.total_respondents || 1;
  const pct = data.percentile || 0;
  const topPct = Math.max(1, 100 - pct);
  const dots = Math.min(total, 20);
  const filledDots = Math.max(1, Math.round((pct / 100) * dots));

  return (
    <div className="waffle">
      <div className="waffle__score">
        <span className="waffle__pct">{"Top " + topPct + "%"}</span>
        <span className="waffle__label">
          {"Vous faites mieux que "}<strong>{pct + "%"}</strong>{" des participants"}
        </span>
      </div>
      <div className="waffle__grid">
        {Array.from({ length: dots }).map((_, i) => (
          <span
            key={i}
            className={`waffle__dot${i < filledDots ? ' waffle__dot--filled' : ''}`}
            style={{ animationDelay: `${i * 0.04}s` }}
          />
        ))}
      </div>
      <p className="waffle__footnote">
        {"Basé sur "}{total}{" réponses complètes"}
      </p>
    </div>
  );
}

/* ── ④ Nightingale / Radial Lines ─────────────────── */
function NightingaleChart({ data, isGeneral }) {
  const all = data.all_respondents || [];
  const myId = isGeneral ? null : data.my_response_id;
  const secteurs = [...new Set(all.map((r) => r.secteur))];
  const angleStep = (2 * Math.PI) / (all.length || 1);
  const cx = 200;
  const cy = 200;
  const maxR = 170;
  const MY_COLOR = '#facc15'; // gold — distinct from all sector colors

  // Sort by secteur for grouping
  const sorted = [...all].sort((a, b) => {
    const ai = secteurs.indexOf(a.secteur);
    const bi = secteurs.indexOf(b.secteur);
    return ai - bi;
  });

  // Find "me" index and compute its position
  const meIndex = sorted.findIndex((r) => r.response_id === myId);
  const meData = meIndex >= 0 ? sorted[meIndex] : null;

  return (
    <svg viewBox="0 0 400 400" className="nightingale">
      {/* Concentric circles */}
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <circle key={f} cx={cx} cy={cy} r={maxR * f} fill="none" stroke="rgba(0,0,0,.06)" strokeWidth="1" />
      ))}

      {/* Other lines (drawn first, behind) */}
      {sorted.map((r, i) => {
        if (r.response_id === myId) return null;
        const angle = angleStep * i - Math.PI / 2;
        const score = ((r.decision_score + r.research_freq) / 9) * maxR;
        const x2 = cx + Math.cos(angle) * score;
        const y2 = cy + Math.sin(angle) * score;

        return (
          <line
            key={r.response_id}
            x1={cx} y1={cy} x2={x2} y2={y2}
            stroke={SECTEUR_COLORS[r.secteur] || '#9ca3af'}
            strokeWidth={isGeneral ? 2 : 1.5}
            strokeLinecap="round"
            opacity={isGeneral ? 0.65 : 0.4}
          />
        );
      })}

      {/* My line (drawn last, on top) */}
      {meData && (() => {
        const angle = angleStep * meIndex - Math.PI / 2;
        const score = ((meData.decision_score + meData.research_freq) / 9) * maxR;
        const x2 = cx + Math.cos(angle) * score;
        const y2 = cy + Math.sin(angle) * score;
        return (
          <g>
            <line
              x1={cx} y1={cy} x2={x2} y2={y2}
              stroke={MY_COLOR}
              strokeWidth={4}
              strokeLinecap="round"
              filter="drop-shadow(0 0 4px rgba(250,204,21,.5))"
            />
            <circle cx={x2} cy={y2} r="6" fill={MY_COLOR} stroke="#fff" strokeWidth="2.5" />
            <text
              x={x2 + (x2 > cx ? 10 : -10)} y={y2 - 10}
              fontSize="10" fontWeight="700" fill={MY_COLOR}
              fontFamily="Plus Jakarta Sans"
              textAnchor={x2 > cx ? 'start' : 'end'}
            >Vous</text>
          </g>
        );
      })()}

      {/* Center dot */}
      <circle cx={cx} cy={cy} r="4" fill="var(--bg-dark)" />

      {/* Legend */}
      {secteurs.map((s, i) => (
        <g key={s} transform={`translate(10, ${370 - i * 16})`}>
          <rect width="8" height="8" rx="2" fill={SECTEUR_COLORS[s] || '#9ca3af'} />
          <text x="14" y="8" fontSize="9" fill="#6b7280" fontFamily="Plus Jakarta Sans">{s}</text>
        </g>
      ))}
      {/* "Vous" in legend — personal only */}
      {!isGeneral && (
        <g transform={`translate(10, ${370 - secteurs.length * 16})`}>
          <rect width="8" height="8" rx="2" fill={MY_COLOR} />
          <text x="14" y="8" fontSize="9" fontWeight="700" fill="#6b7280" fontFamily="Plus Jakarta Sans">Vous</text>
        </g>
      )}
    </svg>
  );
}

/* ── Formatted Text (simple markdown) ──────────────── */
function FormattedText({ text }) {
  if (!text) return null;

  const lines = text.split('\n').filter((l) => l.trim() !== '');

  return (
    <div className="formatted-text">
      {lines.map((line, i) => {
        const trimmed = line.trim();

        // Heading: ## Title
        const headingMatch = trimmed.match(/^#{1,3}\s+(.+)$/);
        if (headingMatch) {
          return <h4 key={i} className="formatted-text__heading">{headingMatch[1]}</h4>;
        }

        // List item: starts with - or •
        const isListItem = /^[-•]\s/.test(trimmed);
        const content = isListItem ? trimmed.replace(/^[-•]\s*/, '') : trimmed;

        // Process **bold** within the line
        const parts = content.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });

        if (isListItem) {
          return (
            <div key={i} className="formatted-text__li">
              <span className="formatted-text__bullet" />
              <span>{parts}</span>
            </div>
          );
        }

        return <p key={i} className="formatted-text__p">{parts}</p>;
      })}
    </div>
  );
}

/* ── ④ Synthesis Section ───────────────────────────── */
function SynthesisSection({ surveyId, sessionToken }) {
  const [synthesis, setSynthesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcriptionPending, setTranscriptionPending] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  // Poll transcription status then fetch synthesis
  useEffect(() => {
    let cancelled = false;

    const fetchSynthesis = async () => {
      try {
        // Check if transcriptions are done
        const statusRes = await client.get('/chatbot/transcription/status', {
          params: { token: sessionToken, survey_id: surveyId },
        });
        const pending = statusRes.data?.data?.pending;

        if (pending && pollCount < 30) {
          setTranscriptionPending(true);
          setLoading(true);
          // Poll again in 5 seconds
          setTimeout(() => {
            if (!cancelled) setPollCount((c) => c + 1);
          }, 5000);
          return;
        }

        setTranscriptionPending(false);

        // Fetch synthesis
        const res = await client.get('/chatbot/results/synthesis', {
          params: { token: sessionToken, survey_id: surveyId },
        });
        if (cancelled) return;

        const data = res.data?.data || null;

        // If any question has my_answer but no personal_synthesis yet, retry
        const missingPersonal = data?.questions?.some(
          (q) => q.my_answer && !q.personal_synthesis
        );
        if (missingPersonal && pollCount < 20) {
          setTimeout(() => {
            if (!cancelled) setPollCount((c) => c + 1);
          }, 3000);
          // Still set partial data so group synthesis shows immediately
          setSynthesis(data);
          return;
        }

        setSynthesis(data);
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSynthesis();
    return () => { cancelled = true; };
  }, [surveyId, sessionToken, pollCount]);

  if (loading) {
    return (
      <section className="results__card results__card--synthesis">
        <h2 className="results__card-title">Analyse qualitative</h2>
        <div className="analyzing-indicator analyzing-indicator--large">
          <span className="analyzing-indicator__text">Analysing</span>
          <span className="analyzing-indicator__dots">
            <span /><span /><span />
          </span>
        </div>
      </section>
    );
  }

  if (!synthesis || !synthesis.questions || synthesis.questions.length === 0) {
    return null;
  }

  return (
    <>
      {synthesis.questions.map((q) => (
        <section key={q.question_id} className="results__card results__card--synthesis">
          <div className="synthesis__header">
            <div className="synthesis__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="results__card-title" style={{ marginBottom: 0 }}>Analyse qualitative</h2>
              <p className="synthesis__question-text">{q.question_text}</p>
            </div>
          </div>

          {q.transcription_pending && (
            <div className="analyzing-indicator">
              <span className="analyzing-indicator__text">Analysing</span>
              <span className="analyzing-indicator__dots">
                <span /><span /><span />
              </span>
            </div>
          )}

          {q.group_synthesis && (
            <div className="synthesis__group">
              <div className="synthesis__group-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span className="synthesis__group-title">
                  {"Tendance générale"}
                </span>
              </div>
              <div className="synthesis__group-body">
                <FormattedText text={q.group_synthesis} />
              </div>
            </div>
          )}

          {q.personal_synthesis && (
            <div className="synthesis__personal">
              <div className="synthesis__personal-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span className="synthesis__personal-title">Votre positionnement</span>
              </div>
              <div className="synthesis__personal-body">
                <FormattedText text={q.personal_synthesis} />
              </div>
            </div>
          )}
        </section>
      ))}
    </>
  );
}

/* ── General Synthesis (no personal data) ──────────── */
/* ══════════════════════════════════════════════════════
   CONFIG-DRIVEN CHART COMPONENTS
   ══════════════════════════════════════════════════════ */

const ROSE_COLORS = ['#fabc02', '#f97316', '#8b5cf6', '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#14b8a6'];

/* ── Nightingale Rose (sector votes) ───────────────── */
function NightingaleRoseChart({ data: d, isGeneral }) {
  const votes = d.votes || [];
  const myCode = isGeneral ? null : d.my_code;
  const realTotal = d.total || votes.reduce((s, v) => s + v.count, 0) || 1;
  const total = getInflatedTotal(realTotal);
  const maxCount = Math.max(...votes.map((v) => v.count), 1);
  const cx = 200;
  const cy = 200;
  const maxR = 155;
  const minR = 44;

  // Keep backend order (custom order defined in controller)
  const sorted = [...votes].map((v, i) => ({ ...v, origIndex: i }));

  const angleStep = (2 * Math.PI) / (sorted.length || 1);
  const gap = 0.05;

  const PETAL_COLORS = ['#fabc02', '#f97316', '#8b5cf6', '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#14b8a6'];

  return (
    <div className="rose-chart">
      <svg viewBox="0 0 400 400" className="nightingale">
        {/* Wedges */}
        {sorted.map((v, i) => {
          const startAngle = angleStep * i - Math.PI / 2 + gap / 2;
          const endAngle = angleStep * (i + 1) - Math.PI / 2 - gap / 2;
          const r = minR + ((v.count / maxCount) * (maxR - minR));
          const isMe = v.code === myCode;

          const x1 = cx + Math.cos(startAngle) * r;
          const y1 = cy + Math.sin(startAngle) * r;
          const x2 = cx + Math.cos(endAngle) * r;
          const y2 = cy + Math.sin(endAngle) * r;
          const ix1 = cx + Math.cos(startAngle) * minR;
          const iy1 = cy + Math.sin(startAngle) * minR;
          const ix2 = cx + Math.cos(endAngle) * minR;
          const iy2 = cy + Math.sin(endAngle) * minR;
          const large = endAngle - startAngle > Math.PI ? 1 : 0;

          const path = [
            `M ${ix1} ${iy1}`,
            `A ${minR} ${minR} 0 ${large} 1 ${ix2} ${iy2}`,
            `L ${x2} ${y2}`,
            `A ${r} ${r} 0 ${large} 0 ${x1} ${y1}`,
            'Z',
          ].join(' ');

          const midAngle = (startAngle + endAngle) / 2;
          const pct = Math.round((v.count / realTotal) * 100);
          const color = PETAL_COLORS[i % PETAL_COLORS.length];
          const textR = (minR + r) / 2;

          return (
            <g key={v.code} className="rose-chart__wedge">
              <path
                d={path}
                fill={color}
                stroke="#fff"
                strokeWidth="2"
                className="rose-chart__path"
                style={{
                  animationDelay: `${i * 0.07}s`,
                  filter: isMe ? 'drop-shadow(0 0 8px rgba(250,188,2,.4))' : 'none',
                }}
              />
              {/* Percentage — hide if petal too small */}
              {pct >= 9 && (
                <text
                  x={cx + Math.cos(midAngle) * textR}
                  y={cy + Math.sin(midAngle) * textR}
                  textAnchor="middle" dominantBaseline="middle"
                  fontSize="14" fontWeight="700" fill="#fff"
                  fontFamily="Plus Jakarta Sans"
                >
                  {pct + '%'}
                </text>
              )}
            </g>
          );
        })}

        {/* Center circle — total count */}
        <circle cx={cx} cy={cy} r={minR - 3} fill="#0f1729" />
        <circle cx={cx} cy={cy} r={minR - 5} fill="var(--bg-card)" />
        <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle"
          fontSize="24" fontWeight="400" fill="var(--text)" fontFamily="DM Serif Display">
          {total}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" dominantBaseline="middle"
          fontSize="7" fontWeight="700" fill="var(--text-light)" fontFamily="Plus Jakarta Sans"
          letterSpacing=".08em">
          VOTES
        </text>
      </svg>

      {/* Legend */}
      <div className="rose-chart__legend">
        {sorted.map((v, i) => {
          const isMe = v.code === myCode;
          const color = PETAL_COLORS[i % PETAL_COLORS.length];
          const pct = Math.round((v.count / realTotal) * 100);
          return (
            <div key={v.code} className={`rose-chart__legend-item${isMe ? ' rose-chart__legend-item--me' : ''}`}>
              <span className="rose-chart__legend-dot" style={{ background: color }} />
              <span className="rose-chart__legend-label">{v.label}</span>
              <span className="rose-chart__legend-pct">{pct}%</span>
              {isMe && <span className="rose-chart__legend-badge">votre profil</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Config Bubble Scatter ─────────────────────────── */
function ConfigBubbleChart({ data: d, isGeneral, chart }) {
  const points = d.points || [];
  const myPoint = isGeneral ? null : d.my_point;
  const xLabels = d.x_labels || {};
  const xIsScale = d.x_is_scale || false;
  const xMin = d.x_min ?? 0;
  const xMax = d.x_max || 5;
  const yMin = d.y_min ?? 0;
  const yMax = d.y_max ?? 5;

  const others = myPoint ? points.filter((p) => p.response_id !== myPoint.response_id) : points;

  const datasets = useMemo(() => {
    const ds = [
      {
        label: 'Participants',
        data: others.map((p) => ({
          x: p.x,
          y: p.y,
          r: 7,
        })),
        backgroundColor: 'rgba(156, 163, 175, .35)',
        borderColor: 'rgba(156, 163, 175, .5)',
        borderWidth: 1,
      },
    ];

    if (myPoint) {
      ds.push({
        label: 'Vous',
        data: [{ x: myPoint.x, y: myPoint.y, r: 12 }],
        backgroundColor: 'rgba(250, 188, 2, .7)',
        borderColor: '#fabc02',
        borderWidth: 2.5,
      });
    }

    return ds;
  }, [others, myPoint]);

  const xTickLabels = { 0: '' };
  Object.entries(xLabels).forEach(([k, label]) => {
    xTickLabels[Number(k)] = label.length > 16 ? label.substring(0, 14) + '\u2026' : label;
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { family: "'Plus Jakarta Sans'" }, usePointStyle: true, padding: 20 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `(${Math.round(ctx.raw.x)}, ${Math.round(ctx.raw.y)})`,
        },
      },
    },
    scales: {
      x: {
        min: xIsScale ? Math.max(0, xMin - 0.5) : 0.5, max: xMax + 0.5,
        title: { display: true, text: (chart?.x_label || 'X').substring(0, 35) + ' \u2192', font: { family: "'Plus Jakarta Sans'", size: 10, weight: '600' } },
        grid: { color: 'rgba(0,0,0,.04)' },
        ticks: { stepSize: 1, font: { size: 9 }, ...(xIsScale ? {} : { callback: (v) => xTickLabels[v] || '' }) },
      },
      y: {
        min: Math.max(0, yMin - 0.5), max: yMax + 0.5,
        title: { display: true, text: (chart?.y_label || d.y_question_text || 'Y').substring(0, 30) + ' \u2192', font: { family: "'Plus Jakarta Sans'", size: 10, weight: '600' } },
        grid: { color: 'rgba(0,0,0,.04)' },
        ticks: { stepSize: 1 },
      },
    },
  };

  return <Bubble data={{ datasets }} options={options} />;
}

/* ── Config Nightingale Lines ──────────────────────── */
function ConfigNightingaleLines({ data: d, isGeneral }) {
  const respondents = d.respondents || [];
  const myId = isGeneral ? null : d.my_response_id;
  const maxScore = d.max_score || 5;
  const secteurs = [...new Set(respondents.map((r) => r.secteur))];
  const angleStep = (2 * Math.PI) / (respondents.length || 1);
  const cx = 200;
  const cy = 200;
  const maxR = 170;
  const MY_COLOR = '#facc15';

  const sorted = [...respondents].sort((a, b) => secteurs.indexOf(a.secteur) - secteurs.indexOf(b.secteur));
  const meIndex = sorted.findIndex((r) => r.response_id === myId);
  const meData = meIndex >= 0 ? sorted[meIndex] : null;

  return (
    <svg viewBox="0 0 400 400" className="nightingale">
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <circle key={f} cx={cx} cy={cy} r={maxR * f} fill="none" stroke="rgba(0,0,0,.06)" strokeWidth="1" />
      ))}

      {sorted.map((r, i) => {
        if (r.response_id === myId) return null;
        const angle = angleStep * i - Math.PI / 2;
        const score = (r.score / (maxScore || 5)) * maxR;
        const x2 = cx + Math.cos(angle) * score;
        const y2 = cy + Math.sin(angle) * score;
        return (
          <line key={r.response_id} x1={cx} y1={cy} x2={x2} y2={y2}
            stroke={SECTEUR_COLORS[r.secteur] || '#9ca3af'}
            strokeWidth={isGeneral ? 2 : 1.5} strokeLinecap="round" opacity={isGeneral ? 0.65 : 0.4}
          />
        );
      })}

      {meData && (() => {
        const angle = angleStep * meIndex - Math.PI / 2;
        const score = (meData.score / (maxScore || 5)) * maxR;
        const x2 = cx + Math.cos(angle) * score;
        const y2 = cy + Math.sin(angle) * score;
        return (
          <g>
            <line x1={cx} y1={cy} x2={x2} y2={y2} stroke={MY_COLOR} strokeWidth={4} strokeLinecap="round"
              filter="drop-shadow(0 0 4px rgba(250,204,21,.5))" />
            <circle cx={x2} cy={y2} r="6" fill={MY_COLOR} stroke="#fff" strokeWidth="2.5" />
            <text x={x2 + (x2 > cx ? 10 : -10)} y={y2 - 10} fontSize="10" fontWeight="700" fill={MY_COLOR}
              fontFamily="Plus Jakarta Sans" textAnchor={x2 > cx ? 'start' : 'end'}>Vous</text>
          </g>
        );
      })()}

      <circle cx={cx} cy={cy} r="4" fill="var(--bg-dark)" />

      {secteurs.map((s, i) => (
        <g key={s} transform={`translate(10, ${370 - i * 16})`}>
          <rect width="8" height="8" rx="2" fill={SECTEUR_COLORS[s] || '#9ca3af'} />
          <text x="14" y="8" fontSize="9" fill="#6b7280" fontFamily="Plus Jakarta Sans">{s}</text>
        </g>
      ))}
      {!isGeneral && (
        <g transform={`translate(10, ${370 - secteurs.length * 16})`}>
          <rect width="8" height="8" rx="2" fill={MY_COLOR} />
          <text x="14" y="8" fontSize="9" fontWeight="700" fill="#6b7280" fontFamily="Plus Jakarta Sans">Vous</text>
        </g>
      )}
    </svg>
  );
}

/* ── Word Cloud ────────────────────────────────────── */
const CLOUD_REF_WIDTH = 680;

function WordCloud({ data: d }) {
  const allWords = d.words || [];
  const myKeyword = d.my_keyword || null;

  // Always include participant's keyword in the displayed words
  let words = allWords.slice(0, 15);
  if (myKeyword) {
    // Remove existing instance to re-insert in middle
    words = words.filter((w) => w.text.toLowerCase() !== myKeyword.toLowerCase());
    const myWord =
      allWords.find((w) => w.text.toLowerCase() === myKeyword.toLowerCase())
      || { text: myKeyword, count: 1 };
    // Trim to 14 (we'll add 1) and insert at the middle
    if (words.length >= 15) words = words.slice(0, 14);
    const mid = Math.floor(words.length / 2);
    words = [...words.slice(0, mid), myWord, ...words.slice(mid)];
  }
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [innerH, setInnerH] = useState('auto');

  const rescale = useCallback(() => {
    if (!wrapperRef.current || !innerRef.current) return;
    const wrapW = wrapperRef.current.offsetWidth;
    const s = Math.min(1, wrapW / CLOUD_REF_WIDTH);
    setScale(s);
    setInnerH(innerRef.current.offsetHeight * s);
  }, []);

  useEffect(() => {
    rescale();
    window.addEventListener('resize', rescale);
    return () => window.removeEventListener('resize', rescale);
  }, [rescale, words]);

  const isPending = d.my_keyword_pending;
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isPending]);

  // Generate stable random delays per word (seeded by word text)
  const randomDelays = useMemo(() => {
    return words.map((w) => {
      let hash = 0;
      for (let i = 0; i < w.text.length; i++) hash = ((hash << 5) - hash + w.text.charCodeAt(i)) | 0;
      return (Math.abs(hash) % 600) / 1000; // 0 to 0.6s
    });
  }, [words]);

  if (words.length === 0 && !isPending) return <p className="results__card-desc">Pas encore de données.</p>;

  const maxCount = Math.max(...words.map((w) => w.count), 1);
  const grays = ['#0f1729', '#1a1a2e', '#374151', '#4b5563', '#6b7280', '#9ca3af', '#d1d5db'];

  if (isPending) {
    return (
      <div className="word-cloud" ref={observerRef}>
        <div className="word-cloud__analyzing">
          <div className="analyzing-indicator analyzing-indicator--large">
            <span className="analyzing-indicator__text">Analysing</span>
            <span className="analyzing-indicator__dots">
              <span /><span /><span />
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="word-cloud" ref={observerRef}>
      <div className="word-cloud__wrapper" ref={wrapperRef} style={{ height: innerH }}>
        <div
          className="word-cloud__canvas"
          ref={innerRef}
          style={{ transform: `scale(${scale})` }}
        >
          {words.map((w, i) => {
            const norm = w.count / maxCount;
            const size = 0.8 + norm * 2.2;
            const weight = norm > 0.6 ? 700 : norm > 0.3 ? 600 : 400;
            const isMine = myKeyword && w.text.toLowerCase() === myKeyword.toLowerCase();
            // My word: force a large size, ignore frequency
            const finalSize = isMine ? 4.2 : size;

            return (
              <span
                key={w.text}
                className={`word-cloud__word${isVisible ? ' word-cloud__word--visible' : ''}`}
                data-mine={isMine || undefined}
                style={{
                  fontSize: `${finalSize}rem`,
                  fontWeight: weight,
                  color: isMine ? undefined : grays[i % grays.length],
                  animationDelay: isVisible ? `${randomDelays[i]}s` : undefined,
                }}
              >
                {w.text}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Profile Results ────────────────────────────────── */
function ProfileResults({ myProfile, profileDefs }) {
  const findDef = (code, type) => {
    const list = profileDefs?.[type] || [];
    return list.find((p) => p.code === code);
  };

  const dPrimary = findDef(myProfile.d_primary, 'dirigeant');
  const dSecondary = myProfile.d_secondary ? findDef(myProfile.d_secondary, 'dirigeant') : null;
  const ePrimary = findDef(myProfile.e_primary, 'entreprise');
  const eSecondary = myProfile.e_secondary ? findDef(myProfile.e_secondary, 'entreprise') : null;

  return (
    <section className="results__card results__card--profiles">
      <h2 className="results__card-title">Votre diagnostic</h2>
      <p className="results__card-desc">
        {"Votre posture de dirigeant et le profil de votre organisation, révélés par vos réponses."}
      </p>

      <div className="profile-results">
        {/* Dirigeant */}
        <div className="profile-results__section">
          <span className="profile-results__section-label">Profil Dirigeant</span>
          {dPrimary && (
            <ProfileCard profile={dPrimary} isPrimary gap={myProfile.d_gap} />
          )}
          {dSecondary && (
            <ProfileCard profile={dSecondary} isPrimary={false} gap={myProfile.d_gap} />
          )}
        </div>

        {/* Entreprise */}
        <div className="profile-results__section">
          <span className="profile-results__section-label">Profil Entreprise</span>
          {ePrimary && (
            <ProfileCard profile={ePrimary} isPrimary gap={myProfile.e_gap} />
          )}
          {eSecondary && (
            <ProfileCard profile={eSecondary} isPrimary={false} gap={myProfile.e_gap} />
          )}
        </div>

        {/* Croisement */}
        {dPrimary && ePrimary && CROISEMENTS[`${myProfile.d_primary}_${myProfile.e_primary}`] && (
          <div className="profile-results__section">
            <span className="profile-results__section-label">Votre croisement</span>
            <div className="profile-croisement">
              <h3 className="profile-croisement__title">
                {dPrimary.name} <span className="profile-croisement__x">&times;</span> {ePrimary.name}
              </h3>
              <p className="profile-croisement__text">
                {CROISEMENTS[`${myProfile.d_primary}_${myProfile.e_primary}`]}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const CROISEMENTS = {
  'D1_E1': "Le duo du contrôle prudent. Le dirigeant sécurise et l'entreprise adore les garde-corps. C'est solide, discipliné, rassurant, mais le risque est de fabriquer une organisation trop fermée, qui confond maîtrise et raideur.",
  'D2_E1': "Un dirigeant qui raconte l'avenir dans une organisation qui aime verrouiller le présent. C'est une combinaison intéressante pour transformer sans panique, mais frustrante si la forteresse étouffe le récit ou si le récit ne change rien aux mécanismes réels.",
  'D3_E1': "Un dirigeant qui pousse fort dans une organisation qui contrôle fort. Cela peut produire une exécution disciplinée et très efficace. Mais la combinaison peut aussi devenir dure, verticale et peu respirable.",
  'D4_E1': "Le dirigeant évite les mauvaises nouvelles dans une entreprise déjà portée vers le contrôle et la fermeture. C'est la configuration du silence confortable. Elle peut durer un moment, mais les vrais problèmes remontent tard et souvent trop brutalement.",
  'D5_E1': "Le duo du statu quo maîtrisé. Le dirigeant ne bouge pas trop, l'entreprise n'aime pas trop bouger non plus. Très stable à court terme, mais vulnérable à l'érosion lente, à l'obsolescence et aux occasions manquées.",
  'D1_E2': "Un dirigeant protecteur à la tête d'une organisation débrouillarde. Il tente de mettre des garde-fous dans un système qui fonctionne surtout grâce à l'intelligence pratique. Le potentiel est bon si la protection devient structuration ; sinon, le chef passe son temps à contenir des bricolages.",
  'D2_E2': "Le duo de la projection et de la débrouille. Le dirigeant donne une histoire, les équipes trouvent toujours un moyen de livrer. Très énergique, très vivant, mais le risque est de maquiller le manque de structure avec un grand récit inspirant.",
  'D3_E2': "Le chef veut aller vite ; l'entreprise compense comme elle peut. Cela donne souvent des exploits ponctuels, une grande intensité et beaucoup d'énergie. Le risque majeur est l'usure : on exige de la performance d'une mécanique encore artisanale.",
  'D4_E2': "L'entreprise compense pendant que le dirigeant détourne le regard. Les équipes tiennent la boutique, parfois admirablement, mais sans arbitrage clair. Le risque est de transformer la débrouille en destin.",
  'D5_E2': "L'entreprise se débrouille seule pendant que le dirigeant évite les grands mouvements. Cela peut donner une étonnante résilience quotidienne. Mais sans impulsion, la débrouille ne se transforme jamais en système robuste.",
  'D1_E3': "Profil paradoxal mais fréquent : un dirigeant protecteur sur une entreprise très offensive. Il essaye de canaliser une force d'exécution très forte. S'il réussit, il civilise la performance ; s'il échoue, il est perçu comme un frein interne.",
  'D2_E3': "Configuration redoutable quand elle est saine : vision claire et exécution puissante. C'est le couple des offensives bien racontées. Le risque est de devenir tellement convaincant qu'on ne voit plus les angles morts humains ou opérationnels.",
  'D3_E3': "Le duo le plus offensif de la matrice. Cap, vitesse, exécution, impact. C'est très puissant pour conquérir. Le risque est de finir par confondre résultat et brutalité, et de perdre en qualité d'écoute, d'innovation ou de rétention des talents.",
  'D4_E3': "Configuration instable : la machine avance, mais le chef évite de voir ce qu'elle abîme ou ce qu'elle cache. On peut obtenir des résultats impressionnants pendant un temps, puis une crise très brutale quand la réalité réclame l'addition.",
  'D5_E3': "Le chef temporise, la machine veut avancer. La tension est forte : soit le dirigeant ralentit une force utile, soit la machine finit par se gouverner elle-même. Cette combinaison exige des arbitrages clairs pour ne pas produire de la frustration à tous les niveaux.",
  'D1_E4': "Le dirigeant sent que quelque chose peut casser et multiplie donc les sécurités. Son intuition est souvent juste : la boîte paraît plus solide qu'elle ne l'est. Le danger est de protéger les symptômes au lieu de reconstruire le socle.",
  'D2_E4': "Ici, le récit peut tenir l'édifice plus que le système lui-même. Le dirigeant sait donner confiance, parfois même quand les fondations sont précaires. C'est une zone très séduisante… et très dangereuse si la narration remplace la consolidation.",
  'D3_E4': "Le dirigeant pousse une structure plus fragile qu'elle n'en a l'air. Les résultats peuvent sembler impressionnants à court terme, mais la pression révèle très vite les faiblesses cachées. C'est une configuration à haut risque de rupture.",
  'D4_E4': "C'est l'une des zones les plus risquées de la matrice. Le dirigeant ne veut pas voir ; l'entreprise tient mal. Tout peut sembler acceptable jusqu'au moment où un appui cède. La chute est souvent plus rapide que prévue.",
  'D5_E4': "Le pire risque ici est la procrastination structurelle. Le dirigeant remet, l'entreprise repose sur des fragilités, et tout le monde espère que ça tiendra encore un peu. C'est souvent le scénario des problèmes connus mais jamais traités.",
  'D1_E5': "Le chef protège un bel objet, une entreprise bien présentée, mais parfois plus brillante que profonde. Il peut aider à mettre de la substance derrière la forme, à condition de ne pas se contenter de défendre l'image.",
  'D2_E5': "Le couple le plus exposé au décalage entre image et réalité. Tout peut sembler cohérent, désirable, premium. Si la substance suit, c'est puissant. Si elle ne suit pas, on obtient une entreprise très bien racontée mais facilement remplaçable.",
  'D3_E5': "Le chef veut prouver par l'action la valeur d'une entreprise très bien packagée. Cela peut fonctionner si l'exécution rattrape la promesse. Sinon, on accélère surtout le moment où le marché verra l'écart entre surface et substance.",
  'D4_E5': "Le regard se détourne pendant que l'apparence rassure. On reste dans un confort esthétique, stratégique ou politique. Mais le marché, lui, finit toujours par distinguer le vrai du joli.",
  'D5_E5': "La belle apparence suffit longtemps à justifier l'inaction. Comme l'objet paraît propre, différencié et présentable, on reporte les décisions de fond. Le danger est de se réveiller quand le vernis ne suffit plus.",
};

const PROFILE_ICONS = {
  D1: (c) => <ShieldCheck size={22} color={c} />,
  D2: (c) => <Clapperboard size={22} color={c} />,
  D3: (c) => <ShieldAlert size={22} color={c} />,
  D4: (c) => <EyeOff size={22} color={c} />,
  D5: (c) => <Minus size={22} color={c} />,
  E1: (c) => <Castle size={22} color={c} />,
  E2: (c) => <Wrench size={22} color={c} />,
  E3: (c) => <Swords size={22} color={c} />,
  E4: (c) => <Layers size={22} color={c} />,
  E5: (c) => <Gem size={22} color={c} />,
};

function ProfileCard({ profile, isPrimary, gap }) {
  const clarity = gap >= 3 ? 'net' : gap >= 1 ? 'nuancé' : 'ex æquo';
  const IconFn = PROFILE_ICONS[profile.code];

  return (
    <div className={`profile-card${isPrimary ? ' profile-card--primary' : ' profile-card--secondary'}`}
      style={{ borderColor: profile.color + (isPrimary ? '60' : '30') }}>
      <div className="profile-card__header">
        <span className="profile-card__icon">
          {IconFn ? IconFn(profile.color) : null}
        </span>
        <div className="profile-card__title-wrap">
          <span className="profile-card__name" style={{ color: isPrimary ? profile.color : 'var(--text-muted)' }}>
            {profile.name}
          </span>
          {isPrimary && (
            <span className="profile-card__badge">
              {"Profil " + clarity}
            </span>
          )}
          {!isPrimary && (
            <span className="profile-card__badge profile-card__badge--secondary">
              Secondaire
            </span>
          )}
        </div>
      </div>
      <p className="profile-card__meaning">{profile.meaning}</p>
      <div className="profile-card__traits">
        <div className="profile-card__trait">
          <span className="profile-card__trait-label profile-card__trait-label--force">Force</span>
          <span className="profile-card__trait-value">{profile.force}</span>
        </div>
        <div className="profile-card__trait">
          <span className="profile-card__trait-label profile-card__trait-label--risque">Risque</span>
          <span className="profile-card__trait-value">{profile.risque}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Matrix Bubble ──────────────────────────────────── */
function MatrixBubble({ allProfiles, myProfile }) {
  if (!allProfiles || allProfiles.length === 0) return null;

  const cellW = 72;
  const cellH = 62;
  const labelW = 100;
  const labelH = 40;
  const gridW = D_CODES.length * cellW;
  const gridH = E_CODES.length * cellH;
  const W = labelW + gridW + 10;
  const H = gridH + labelH + 10;
  const gridX = labelW;
  const gridY = 0;

  // Count per cell
  const cellCounts = {};
  allProfiles.forEach((p) => {
    const key = `${p.d_primary}_${p.e_primary}`;
    cellCounts[key] = (cellCounts[key] || 0) + 1;
  });

  const maxCount = Math.max(...Object.values(cellCounts), 1);
  const maxR = Math.min(cellW, cellH) / 2 - 5;
  const myKey = myProfile ? `${myProfile.d_primary}_${myProfile.e_primary}` : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="matrix-bubble">
      {/* Grid lines */}
      {Array.from({ length: D_CODES.length + 1 }).map((_, i) => (
        <line key={`v${i}`} x1={gridX + i * cellW} y1={gridY} x2={gridX + i * cellW} y2={gridY + gridH}
          stroke="var(--border)" strokeWidth="1" />
      ))}
      {Array.from({ length: E_CODES.length + 1 }).map((_, i) => (
        <line key={`h${i}`} x1={gridX} y1={gridY + i * cellH} x2={gridX + gridW} y2={gridY + i * cellH}
          stroke="var(--border)" strokeWidth="1" />
      ))}

      {/* X labels (Dirigeant) — bottom, multi-line */}
      {D_CODES.map((d, di) => {
        const name = PROFILE_NAMES[d];
        const words = name.split(' ');
        const lines = [];
        let current = '';
        words.forEach((w) => {
          if ((current + ' ' + w).trim().length > 10 && current) { lines.push(current); current = w; }
          else { current = current ? current + ' ' + w : w; }
        });
        if (current) lines.push(current);
        return (
          <text key={`xl${d}`} x={gridX + di * cellW + cellW / 2} y={gridH + 12}
            textAnchor="middle" fontSize="7.5" fontWeight="600" fill="var(--text-muted)" fontFamily="Plus Jakarta Sans">
            {lines.map((l, li) => (
              <tspan key={li} x={gridX + di * cellW + cellW / 2} dy={li === 0 ? 0 : 10}>{l}</tspan>
            ))}
          </text>
        );
      })}

      {/* Y labels (Entreprise) — left, multi-line */}
      {E_CODES.map((e, ei) => {
        const name = PROFILE_NAMES[e];
        const words = name.split(' ');
        const lines = [];
        let current = '';
        words.forEach((w) => {
          if ((current + ' ' + w).trim().length > 12 && current) { lines.push(current); current = w; }
          else { current = current ? current + ' ' + w : w; }
        });
        if (current) lines.push(current);
        const baseY = gridY + (E_CODES.length - 1 - ei) * cellH + cellH / 2 - ((lines.length - 1) * 5);
        return (
          <text key={`yl${e}`} x={gridX - 8} y={baseY}
            textAnchor="end" fontSize="7.5" fontWeight="600" fill="var(--text-muted)" fontFamily="Plus Jakarta Sans">
            {lines.map((l, li) => (
              <tspan key={li} x={gridX - 8} dy={li === 0 ? 0 : 10}>{l}</tspan>
            ))}
          </text>
        );
      })}

      {/* Bubbles */}
      {D_CODES.map((d, di) =>
        E_CODES.map((e, ei) => {
          const key = `${d}_${e}`;
          const count = cellCounts[key] || 0;
          if (count === 0) return null;

          const cx = gridX + di * cellW + cellW / 2;
          const cy = gridY + (E_CODES.length - 1 - ei) * cellH + cellH / 2;
          const r = 5 + (count / maxCount) * (maxR - 5);
          const isMe = key === myKey;

          return (
            <g key={key}>
              <circle cx={cx} cy={cy} r={r}
                fill={isMe ? 'var(--accent)' : '#9ca3af'}
                opacity={isMe ? 0.85 : 0.25}
                stroke={isMe ? 'var(--accent)' : 'none'}
                strokeWidth={isMe ? 2 : 0}
              />
              {r > 8 && (
                <text x={cx} y={cy + 3} textAnchor="middle"
                  fontSize={r > 14 ? '8' : '6.5'} fontWeight="700"
                  fill={isMe ? '#0f1729' : '#6b7280'}
                  fontFamily="Plus Jakarta Sans">
                  {Math.round((count / allProfiles.length) * 100)}%
                </text>
              )}
              {isMe && (
                <text x={cx} y={cy - r - 5} textAnchor="middle"
                  fontSize="8" fontWeight="700" fill="var(--accent)" fontFamily="Plus Jakarta Sans">
                  Vous
                </text>
              )}
            </g>
          );
        })
      )}

      {/* Axis titles */}
      <text x={gridX + gridW / 2} y={H - 1} textAnchor="middle"
        fontSize="9" fontWeight="600" fill="var(--text-light)" fontFamily="Plus Jakarta Sans">
        {"Profil Dirigeant \u2192"}
      </text>
      <text x={8} y={gridH / 2} textAnchor="middle"
        fontSize="9" fontWeight="600" fill="var(--text-light)" fontFamily="Plus Jakarta Sans"
        transform={`rotate(-90, 8, ${gridH / 2})`}>
        {"Profil Entreprise \u2192"}
      </text>
    </svg>
  );
}

/* ── Scatter Aggregate Bar ──────────────────────────── */
const D_CODES = ['D4', 'D5', 'D2', 'D3', 'D1'];
const E_CODES = ['E4', 'E5', 'E2', 'E1', 'E3'];
const PROFILE_COLORS = {
  D1: '#E8C547', D2: '#C46BDB', D3: '#DB4B4B', D4: '#5BA38B', D5: '#4B8FDB',
  E1: '#8B7355', E2: '#DB8B4B', E3: '#6B6B6B', E4: '#DB4B8B', E5: '#6BC4DB',
};
const PROFILE_NAMES = {
  D1: 'Garde-fou', D2: 'Scénariste', D3: 'Commandant', D4: 'Autruche', D5: 'Minimaliste',
  E1: 'Forteresse', E2: 'Système D', E3: 'Machine de guerre', E4: 'Château de cartes', E5: 'Diamant de synthèse',
};

function ScatterAggregateBar({ allProfiles, myProfile, myResponseId }) {
  const chartRef = useRef(null);
  const [isScatter, setIsScatter] = useState(true);
  const [isPinned, setIsPinned] = useState(false);

  const { scatterOption, barOption } = useMemo(() => {
    if (!allProfiles || allProfiles.length === 0) return { scatterOption: null, barOption: null };

    const dLabels = D_CODES.map((c) => PROFILE_NAMES[c]);

    // Group by D profile for scatter
    const groups = {};
    D_CODES.forEach((c) => { groups[c] = []; });
    allProfiles.forEach((p) => {
      if (groups[p.d_primary]) {
        // X = D score total, Y = E score total, for spread
        const dTotal = Object.values(p.d_scores || {}).reduce((s, v) => s + v, 0);
        const eTotal = Object.values(p.e_scores || {}).reduce((s, v) => s + v, 0);
        groups[p.d_primary].push({
          value: [dTotal, eTotal],
          responseId: p.response_id,
          ePrimary: p.e_primary,
        });
      }
    });

    // Scatter option: individual dots grouped by D profile
    const scatterSeries = D_CODES.map((code) => ({
      type: 'scatter',
      id: code,
      name: PROFILE_NAMES[code],
      dataGroupId: code,
      universalTransition: {
        enabled: true,
        delay: () => Math.random() * 400,
      },
      data: groups[code].map((pt) => ({
        value: pt.value,
        ePrimary: pt.ePrimary,
        itemStyle: pt.responseId === myResponseId
          ? { color: '#fabc02', borderColor: '#fff', borderWidth: 2, shadowBlur: 8, shadowColor: 'rgba(250,188,2,.5)' }
          : { color: PROFILE_COLORS[code], opacity: 0.35 },
        symbolSize: pt.responseId === myResponseId ? 16 : 8,
        label: pt.responseId === myResponseId
          ? { show: true, formatter: 'Vous', position: 'top', fontSize: 10, fontWeight: 700, color: '#fabc02', fontFamily: 'Plus Jakarta Sans', distance: 6 }
          : { show: false },
      })),
    }));

    const sOpt = {
      xAxis: {
        scale: true,
        name: 'Score Dirigeant',
        nameLocation: 'center',
        nameGap: 30,
        nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#6b7280', fontFamily: 'Plus Jakarta Sans' },
        axisLine: { lineStyle: { color: '#e5e2dd' } },
        splitLine: { lineStyle: { color: '#f3f0eb' } },
        axisLabel: { fontSize: 9, color: '#9ca3af' },
      },
      yAxis: {
        scale: true,
        name: 'Score Entreprise',
        nameLocation: 'center',
        nameGap: 40,
        nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#6b7280', fontFamily: 'Plus Jakarta Sans' },
        axisLine: { lineStyle: { color: '#e5e2dd' } },
        splitLine: { lineStyle: { color: '#f3f0eb' } },
        axisLabel: { fontSize: 9, color: '#9ca3af' },
      },
      legend: {
        data: D_CODES.map((c) => PROFILE_NAMES[c]),
        bottom: 0,
        textStyle: { fontSize: 10, fontFamily: 'Plus Jakarta Sans', color: '#6b7280' },
        itemWidth: 10, itemHeight: 10,
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(15,23,41,.92)',
        borderColor: 'transparent',
        textStyle: { fontSize: 11, fontFamily: 'Plus Jakarta Sans', color: '#fff' },
        formatter: (params) => {
          const ePrimary = params.data?.ePrimary;
          const eName = ePrimary ? PROFILE_NAMES[ePrimary] : '';
          return `<b>Dirigeant:</b> ${params.seriesName}<br/><b>Entreprise:</b> ${eName}`;
        },
      },
      series: scatterSeries,
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
    };

    // Bar option: aggregate percentage per D profile
    const totalCount = allProfiles.length;
    const dCounts = D_CODES.map((c) => totalCount > 0 ? Math.round((groups[c].length / totalCount) * 100) : 0);

    const bOpt = {
      xAxis: {
        type: 'category',
        data: dLabels,
        axisLabel: { fontSize: 9, fontWeight: 600, color: '#6b7280', fontFamily: 'Plus Jakarta Sans', rotate: 15 },
        axisLine: { lineStyle: { color: '#e5e2dd' } },
        axisTick: { show: false },
      },
      yAxis: {
        name: 'Participants',
        max: 100,
        nameTextStyle: { fontSize: 10, color: '#9ca3af', fontFamily: 'Plus Jakarta Sans' },
        axisLine: { lineStyle: { color: '#e5e2dd' } },
        splitLine: { lineStyle: { color: '#f3f0eb' } },
        axisLabel: { fontSize: 9, color: '#9ca3af', formatter: '{value}%' },
      },
      legend: { show: false },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15,23,41,.92)',
        borderColor: 'transparent',
        textStyle: { fontSize: 11, fontFamily: 'Plus Jakarta Sans', color: '#fff' },
      },
      series: [{
        type: 'bar',
        id: 'total',
        data: dCounts.map((v, i) => ({
          value: v,
          groupId: D_CODES[i],
          itemStyle: {
            color: PROFILE_COLORS[D_CODES[i]],
            borderRadius: [6, 6, 0, 0],
          },
          label: { show: true, position: 'top', fontSize: 11, fontWeight: 700, color: PROFILE_COLORS[D_CODES[i]], fontFamily: 'Plus Jakarta Sans', formatter: (p) => p.value + '%' },
        })),
        universalTransition: {
          enabled: true,
          seriesKey: D_CODES,
          delay: () => Math.random() * 400,
        },
        barWidth: '45%',
      }],
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
    };


    return { scatterOption: sOpt, barOption: bOpt };
  }, [allProfiles, myProfile, myResponseId]);

  // Auto-toggle
  useEffect(() => {
    if (!scatterOption || !barOption || isPinned) return;
    const timer = setInterval(() => {
      setIsScatter((prev) => !prev);
    }, 4000);
    return () => clearInterval(timer);
  }, [scatterOption, barOption, isPinned]);

  // Apply option on toggle
  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance) return;
    instance.setOption(isScatter ? scatterOption : barOption, true);
  }, [isScatter, scatterOption, barOption]);

  if (!scatterOption) return null;

  return (
    <div className="scatter-agg">
      <ReactECharts
        ref={chartRef}
        option={scatterOption}
        style={{ height: 480, width: '100%' }}
        notMerge={true}
      />
      <div className="scatter-agg__toggle">
        <button
          type="button"
          className={`scatter-agg__toggle-btn${isScatter && isPinned ? ' scatter-agg__toggle-btn--active' : ''}`}
          onClick={() => { if (isPinned && isScatter) { setIsPinned(false); } else { setIsScatter(true); setIsPinned(true); } }}
        >
          Scatter
        </button>
        <button
          type="button"
          className={`scatter-agg__toggle-btn${!isScatter && isPinned ? ' scatter-agg__toggle-btn--active' : ''}`}
          onClick={() => { if (isPinned && !isScatter) { setIsPinned(false); } else { setIsScatter(false); setIsPinned(true); } }}
        >
          Aggregate
        </button>
      </div>
    </div>
  );
}

function GeneralSynthesisSection({ syntheses }) {
  return (
    <>
      {syntheses.map((q) => (
        <section key={q.question_id} className="results__card results__card--synthesis">
          <div className="synthesis__header">
            <div className="synthesis__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="results__card-title" style={{ marginBottom: 0 }}>Analyse qualitative</h2>
              <p className="synthesis__question-text">{q.question_text}</p>
            </div>
          </div>

          <div className="synthesis__group">
            <div className="synthesis__group-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="synthesis__group-title">{"Tendance générale"}</span>
            </div>
            <div className="synthesis__group-body">
              <FormattedText text={q.group_synthesis} />
            </div>
          </div>
        </section>
      ))}
    </>
  );
}

export default Results;
